import React, { useState } from 'react';
import '../Styles/MiniCalender.css';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dateKey(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function MiniCalendar({ value, onChange = () => {}, datesWithEvents = new Set(), showFooter = true }) {
  const today = new Date();

  // View state: which month/year is displayed
  const [viewYear, setViewYear] = useState(value ? value.getFullYear() : today.getFullYear());
  const [viewMonth, setViewMonth] = useState(value ? value.getMonth() : today.getMonth());

  const goToPrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  // Build grid: 6 rows × 7 cols, padded with prev/next month days
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1).getDay(); // 0=Sun
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const daysInPrevMonth = new Date(viewYear, viewMonth, 0).getDate();

  const cells = [];
  // Prev month tail
  for (let i = firstDayOfMonth - 1; i >= 0; i--) {
    cells.push({ date: new Date(viewYear, viewMonth - 1, daysInPrevMonth - i), outside: true });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ date: new Date(viewYear, viewMonth, d), outside: false });
  }
  // Next month head — fill to complete last row
  let next = 1;
  while (cells.length % 7 !== 0) {
    cells.push({ date: new Date(viewYear, viewMonth + 1, next++), outside: true });
  }

  const handleDayClick = (date, outside) => {
    if (outside) {
      setViewYear(date.getFullYear());
      setViewMonth(date.getMonth());
    }
    onChange(date);
  };

  const todayFormatted = today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="mc-wrapper">
      {/* Header: Month Year + nav arrows */}
      <div className="mc-header">
        <span className="mc-month-label">{MONTHS[viewMonth]} {viewYear}</span>
        <div className="mc-nav">
          <button className="mc-nav-btn" onClick={goToPrevMonth} aria-label="Previous month">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="18 15 12 9 6 15" />
            </svg>
          </button>
          <button className="mc-nav-btn" onClick={goToNextMonth} aria-label="Next month">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        </div>
      </div>

      {/* Day-of-week headers */}
      <div className="mc-dow-row">
        {DAYS.map(d => (
          <span key={d} className="mc-dow">{d}</span>
        ))}
      </div>

      {/* Day grid */}
      <div className="mc-grid">
        {cells.map(({ date, outside }, idx) => {
          const isToday = isSameDay(date, today);
          const isSelected = value && isSameDay(date, value);
          const hasEvent = datesWithEvents.has(dateKey(date));

          let cellClass = 'mc-day';
          if (outside) cellClass += ' mc-day--outside';
          if (isToday && !isSelected) cellClass += ' mc-day--today';
          if (isSelected) cellClass += ' mc-day--selected';

          return (
            <button
              key={idx}
              className={cellClass}
              onClick={() => handleDayClick(date, outside)}
              aria-label={date.toDateString()}
              aria-pressed={isSelected}
            >
              <span className="mc-day-num">{date.getDate()}</span>
              {hasEvent && <span className="mc-dot" />}
            </button>
          );
        })}
      </div>

      {/* Footer: Today label + date */}
      {showFooter && (
        <div className="mc-footer">
          <span className="mc-footer-label">Today</span>
          <button
            className="mc-footer-date"
            onClick={() => { onChange(today); setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); }}
          >
            {todayFormatted}
          </button>
        </div>
      )}
    </div>
  );
}

export default MiniCalendar;